/**
 * Générateur — Automatisme : relations entre 1/4, 1/2, 3/4 et 1
 * Attendu ID : 1011 | Niveau : 6ème | Domaine : NC
 * Rubrique : AUTOMATISME (programme 2025) — « égalités à trous ».
 * Labels (cartes exerciseur) :
 *   - Relations entre 1/4, 1/2, 3/4 et 1
 *
 * ── Plan de cartes (2026-08-20) ────────────────────────────────────────────
 * UNE carte, 5 formats. Domaine FINI et LITTÉRAL : tout se compte en QUARTS
 * (a ± b = c avec a, b, c ∈ {1, 2, 3, 4} quarts), soit 12 relations dont
 * les 8 de la liste du programme. STRICTEMENT quarts et demis (les tiers,
 * cinquièmes... relèvent d'« additionner des fractions », autre objectif —
 * frontière A5). Écritures CANONIQUES : 2 quarts s'écrit 1/2, 4 s'écrit 1.
 * INVARIANTS :
 *   - chaque égalité émise est RE-VÉRIFIÉE en quarts entiers (throw) ;
 *   - UN trou par égalité (résultat OU un opérande) → réponse UNIQUE ;
 *   - drag : la banque de tuiles est TOUJOURS {1/4, 1/2, 3/4, 1} — trois
 *     réponses DISTINCTES + le piège restant, zéro bouche-trou possible ;
 *   - saisie : le trou est un NUMÉRATEUR au-dessus d'un dénominateur
 *     affiché (« 1/2 + ?/4 = 3/4 » → 1) : réponse numérique unique, le
 *     piège « taper 2/4 pour 1/2 » ne peut pas exister ;
 *   - QCM : les choix sont TOUJOURS les 4 écritures canoniques (cf. 1005) ;
 *   - V/F : les lignes fausses remplacent le résultat par une autre des 4
 *     valeurs, re-vérifiées FAUSSES.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeClasser, tex,
} from '../../../utils';

const ATTENDU_ID = 1011;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Relations entre 1/4, 1/2, 3/4 et 1';

// ─── Le domaine, en QUARTS entiers ─────────────────────────────────────────

/** Écriture LaTeX canonique d'une valeur en quarts (1..4). */
const qTex = (k) => (
    k === 1 ? '\\dfrac{1}{4}' : k === 2 ? '\\dfrac{1}{2}' : k === 3 ? '\\dfrac{3}{4}' : '1'
);
/** Mot BRUT (tuile de drag, comparaison) : « 1/4 », « 1/2 », « 3/4 », « 1 ». */
const qBrut = (k) => (k === 1 ? '1/4' : k === 2 ? '1/2' : k === 3 ? '3/4' : '1');
/**
 * Écriture à NUMÉRATEUR TROUABLE : le dénominateur reste affiché.
 * k pair → sur 2 (1/2 = {{1}}/2, 1 = {{2}}/2) ; k impair → sur 4.
 */
const qDen = (k) => (k % 2 === 0 ? 2 : 4);
const qNum = (k) => (k % 2 === 0 ? k / 2 : k);

/**
 * Les 12 relations a ± b = c (en quarts). Les 8 de la liste du programme
 * en font partie ; les 4 autres (ex. 1 − 3/4) sont les mêmes relations
 * lues autrement — même périmètre.
 */
const RELATIONS = [];
for (let a = 1; a <= 4; a++) {
    for (let b = 1; b <= 4; b++) {
        if (a + b <= 4) RELATIONS.push({ op: '+', a, b, c: a + b });
        if (a - b >= 1) RELATIONS.push({ op: '-', a, b, c: a - b });
    }
}

/** item = { rel, trou ∈ {'res','op1','op2'}, sens ∈ {0,1} }. */
const reponseDe = (item) => (
    item.trou === 'res' ? item.rel.c : item.trou === 'op1' ? item.rel.a : item.rel.b
);

const verifierItem = (item) => {
    const { op, a, b, c } = item.rel;
    const calc = op === '+' ? a + b : a - b;
    if (calc !== c || c < 1 || c > 4 || a < 1 || a > 4 || b < 1 || b > 4) {
        throw new Error(`gen_1011 : relation fausse (${a} ${op} ${b} = ${c})`);
    }
    return item;
};

/**
 * L'égalité en LaTeX (sans les $). `slot` : null (pleine), 'trou' (le
 * marqueur — mot BRUT pour un membre entier, numérateur pour la saisie),
 * 'question' (?). `numerique` : le trou est le NUMÉRATEUR (saisie).
 */
const texEgalite = (item, slot = null, numerique = false) => {
    const { op, a, b, c } = item.rel;
    const rep = reponseDe(item);
    const marque = () => {
        if (slot === 'question') return '?';
        // Saisie : trou-NUMÉRATEUR ou trou-DÉNOMINATEUR (50/50) — sans ce
        // mélange, les mots attendus étaient « 1 » à 77 % (numérateurs
        // canoniques de 1/4, 1/2 et 1), mesuré en revue 2026-08-20 : taper
        // « 1 » partout marquait 3 trous sur 4. La valeur 1 reste trouée
        // telle quelle (« 2/2 » jugé déroutant, arbitrage prof).
        if (numerique) {
            if (rep === 4) return '{{1}}';
            // biais dénominateur (2/3) quand le numérateur vaudrait 1 :
            // équilibre la distribution des mots sur {1, 2, 3, 4}
            const pNum = rep === 3 ? 0.5 : 1 / 3;
            return Math.random() < pNum
                ? `\\dfrac{{{${qNum(rep)}}}}{${qDen(rep)}}`
                : `\\dfrac{${qNum(rep)}}{{{${qDen(rep)}}}}`;
        }
        return `{{${qBrut(rep)}}}`;
    };
    const morceau = (role, k) => (slot !== null && item.trou === role ? marque() : qTex(k));
    const gauche = `${morceau('op1', a)} ${op} ${morceau('op2', b)}`;
    const droite = morceau('res', c);
    return item.sens === 0 ? `${gauche} = ${droite}` : `${droite} = ${gauche}`;
};

/**
 * Tire `nb` items de RELATIONS distinctes ; réponses distinctes si demandé
 * (drag : trois tuiles différentes). Boucle bornée, repli = items de la
 * liste du programme.
 */
const tirerItems = (nb, { reponsesDistinctes = false } = {}) => {
    for (let essai = 0; essai < 60; essai++) {
        const pris = [];
        const vues = new Set();
        const reps = new Set();
        for (let t = 0; t < 120 && pris.length < nb; t++) {
            const rel = randomChoice(RELATIONS);
            // Clé COMMUTATIVE pour les sommes : « 1/4 + 1/2 » et
            // « 1/2 + 1/4 » sont jumelles, jamais les deux dans un même
            // tirage (revue 2026-08-20 — les deux ordres restent tirables
            // d'un exercice à l'autre).
            const cle = rel.op === '+'
                ? `+|${Math.min(rel.a, rel.b)}|${Math.max(rel.a, rel.b)}`
                : `-|${rel.a}|${rel.b}`;
            if (vues.has(cle)) continue;
            const item = { rel, trou: randomChoice(['res', 'res', 'op1', 'op2']), sens: randInt(0, 1) };
            // Un trou posé sur une valeur 1 force le mot « 1 » : on re-tire
            // la position une fois sur deux pour équilibrer (revue 2026-08-20).
            if (reponseDe(item) === 4 && Math.random() < 0.5) {
                item.trou = randomChoice(['res', 'op1', 'op2']);
            }
            if (reponsesDistinctes && reps.has(reponseDe(item))) continue;
            vues.add(cle);
            reps.add(reponseDe(item));
            pris.push(verifierItem(item));
        }
        if (pris.length === nb) return pris;
    }
    return [
        { rel: { op: '+', a: 2, b: 2, c: 4 }, trou: 'res', sens: 0 },  // 1/2 + 1/2 = 1
        { rel: { op: '-', a: 4, b: 1, c: 3 }, trou: 'res', sens: 0 },  // 1 − 1/4 = 3/4
        { rel: { op: '+', a: 2, b: 1, c: 3 }, trou: 'op2', sens: 0 },  // 1/2 + ? = 3/4
        { rel: { op: '-', a: 2, b: 1, c: 1 }, trou: 'res', sens: 0 },  // 1/2 − 1/4 = 1/4
    ].slice(0, nb).map(verifierItem);
};

// ─── Format 1 : texte à trous DRAG (le format-roi des égalités à trous) ───

const genEgalitesTrouDrag = () => {
    const items = tirerItems(3, { reponsesDistinctes: true });
    const phrase = items.map((it) => `$${texEgalite(it, 'trou')}$`).join('\n');
    // La banque est TOUJOURS {1/4, 1/2, 3/4, 1} : le piège est la valeur
    // restante — aucune tuile ne peut être juste dans un autre trou (les
    // réponses sont distinctes et chaque égalité n'a qu'une solution).
    const reponses = new Set(items.map((it) => qBrut(reponseDe(it))));
    const distracteurs = [1, 2, 3, 4].map(qBrut).filter((m) => !reponses.has(m));
    return makeTexteTrou(
        phrase,
        '<p><b>Complète chaque égalité avec les bonnes étiquettes.</b></p>',
        { ...opts(L1), mode: 'drag', distracteurs },
    );
};

// ─── Format 2 : texte à trous SAISIE (numérateur, dénominateur affiché) ───

const genEgalitesTrouSaisie = () => {
    const items = tirerItems(4);
    const phrase = items.map((it) => `$${texEgalite(it, 'trou', true)}$`).join('\n');
    return makeTexteTrou(
        phrase,
        '<p><b>Complète chaque égalité (écris le numérateur qui manque).</b></p>',
        { ...opts(L1), mode: 'saisie' },
    );
};

// ─── Format 3 : QCM (les 4 écritures canoniques, toujours) ────────────────

const genEgalitesQcm = () => {
    const item = tirerItems(1)[0];
    const rep = reponseDe(item);
    const texte = '<p><b>Quelle valeur remplace le « ? » ?</b></p>'
        + `<p>${tex(texEgalite(item, 'question'))}</p>`;
    const choix = [1, 2, 3, 4].map((k) => (k === 4 ? '$1$' : `$${qTex(k)}$`));
    const bonne = rep === 4 ? '$1$' : `$${qTex(rep)}$`;
    const payload = makeQcm(choix, bonne, texte, opts(L1), false);
    payload._meta.params = {
        ...item.rel,
        trou: item.trou,
        reponse: qBrut(rep),
        distracteurs: [1, 2, 3, 4].filter((k) => k !== rep).map((k) => ({
            valeur: qBrut(k),
            code: 'valeur_voisine_du_domaine',
        })),
    };
    return payload;
};

// ─── Format 4 : tableau Vrai/Faux ─────────────────────────────────────────

/** Ligne fausse = le résultat remplacé par une AUTRE des 4 valeurs. */
const fausserItem = (item) => {
    const { op, a, b, c } = item.rel;
    const faux = randomChoice([1, 2, 3, 4].filter((k) => k !== c));
    const gauche = `${qTex(a)} ${op} ${qTex(b)}`;
    const calc = op === '+' ? a + b : a - b;
    if (faux === calc) {
        throw new Error('gen_1011 : la ligne censée être fausse est vraie');
    }
    const ligne = item.sens === 0 ? `${gauche} = ${qTex(faux)}` : `${qTex(faux)} = ${gauche}`;
    return ligne;
};

const genEgalitesVraiFaux = () => {
    let lignes = [];
    let etiquettes = [];
    for (let essai = 0; essai < 30; essai++) {
        const items = tirerItems(4);
        const nbVraies = randInt(1, 3);
        const etiq = shuffle([
            ...Array(nbVraies).fill(0),
            ...Array(4 - nbVraies).fill(1),
        ]);
        const ls = items.map((it, i) => (
            etiq[i] === 0 ? `$${texEgalite(it)}$` : `$${fausserItem(it)}$`
        ));
        lignes = ls;
        etiquettes = etiq;
        if (new Set(ls).size === 4) break;
    }
    return makeVraiFaux(
        lignes,
        etiquettes,
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Format 5 : classer ───────────────────────────────────────────────────

/** Expressions par valeur-résultat (en quarts). */
const EXPRESSIONS_PAR_VALEUR = {};
for (const rel of RELATIONS) {
    const clef = rel.c;
    if (!EXPRESSIONS_PAR_VALEUR[clef]) EXPRESSIONS_PAR_VALEUR[clef] = [];
    EXPRESSIONS_PAR_VALEUR[clef].push(`$${qTex(rel.a)} ${rel.op} ${qTex(rel.b)}$`);
}

const genEgalitesClasser = () => {
    const valeurs = shuffle([1, 2, 3, 4]).slice(0, 3);
    const categories = valeurs.map((v) => ({
        // Nom de catégorie en KaTeX (audit fractions en texte, 2026-09-06).
        name: v === 4 ? 'Égal à 1' : `Égal à ${tex(qTex(v))}`,
        items: shuffle([...new Set(EXPRESSIONS_PAR_VALEUR[v])]).slice(0, 2),
    }));
    return makeClasser(
        categories,
        '<p><b>Classe chaque calcul selon son résultat.</b></p>',
        opts(L1),
    );
};

// ─── Contrat distracteurs (référence de revue, format QCM) ────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'les 4 choix sont TOUJOURS les 4 écritures canoniques {1/4, 1/2, 3/4, 1} : distinction par nature, zéro bouche-trou (même doctrine que le gen_1005)',
        'le drag utilise la même banque de 4 : trois réponses distinctes + la valeur restante en piège',
    ],
    egalites_qcm_unique: {
        support: 'une égalité du domaine (a ± b = c en quarts), trou affiché « ? » (résultat ou opérande)',
        fixes: {
            valeur_voisine_du_domaine: 'les trois autres valeurs du domaine — chaque confusion (1/2 + 1/4 = 1/2 ? = 1 ?) est une relation mal mémorisée',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    egalites_texte_trou_drag: genEgalitesTrouDrag,
    egalites_texte_trou_saisie: genEgalitesTrouSaisie,
    egalites_qcm_unique: genEgalitesQcm,
    egalites_tableau_vf: genEgalitesVraiFaux,
    egalites_classer: genEgalitesClasser,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    tableau_vf: 'Vrai/Faux', classer: 'Classer',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'egalites', label: L1, formatsList: buildFormats('egalites') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
